import Avatar from '@shared/components/Avatar/Avatar';
import Tooltip from '@shared/components/Tooltip/Tooltip';
import { useTranslation } from 'react-i18next';

import { ReactComponent as CloseSmallIcon } from 'assets/icons/CloseSmall.svg';
import { ReactComponent as MailIcon } from 'assets/icons/Mail.svg';
import { ReactComponent as MatrixIcon } from 'assets/icons/Matrix.svg';
import CenteredSpinner from 'components/CenteredSpinner/CenteredSpinner';
import {
  isEmailFormat,
  isMatrixIdFormat,
} from 'modules/CreateWorkspaceModal/WorkspaceInviteStep/WorkspaceInviteForm/utils';
import useExistingRoomMemberIds from 'modules/Messenger/components/InviteMembersField/useExistingRoomMemberIds';

import type { InviteInputMode } from './InviteParticipantsSection';
import OrganisationMembersList from './OrganisationMembersList';
import SuggestedMembersList from './SuggestedMembersList';

interface InviteParticipantsFieldProps {
  workspaceId: string | null | undefined;
  searchTerm: string;
  participants: string[];
  onToggle: (userId: string, displayName?: string) => void;
  displayNameMap: Record<string, string>;
  inviteInputMode: InviteInputMode;
  /** When inviting to an existing meeting/chat room, hide members already joined or invited there. */
  excludeRoomId?: string;
}

interface Candidate {
  userId: string;
  displayName: string;
}

type ChipVariant = 'email' | 'known' | 'matrix';

function getChipVariant(userId: string, displayNameMap: Record<string, string>): ChipVariant {
  if (isEmailFormat(userId)) return 'email';
  if (displayNameMap[userId] !== undefined) return 'known';
  if (isMatrixIdFormat(userId)) return 'matrix';
  return 'known';
}

const InviteParticipantsField = ({
  workspaceId,
  searchTerm,
  participants,
  onToggle,
  displayNameMap,
  inviteInputMode,
  excludeRoomId,
}: InviteParticipantsFieldProps) => {
  const { t } = useTranslation();
  const matrixOnly = inviteInputMode === 'matrixIdOnly';
  const { memberIds: existingRoomMemberIds, isReady: excludeRoomMembersReady } =
    useExistingRoomMemberIds(excludeRoomId);

  const selectedCandidates: Candidate[] = participants.map((userId) => ({
    userId,
    displayName: displayNameMap[userId] ?? userId,
  }));

  const useOrganisationList = !!workspaceId;

  const renderChipAvatar = (c: Candidate, dnm: Record<string, string>) => {
    const variant = getChipVariant(c.userId, dnm);
    if (variant === 'email') {
      return (
        <div className="flex shrink-0 items-center justify-center w-10 h-10 rounded-xl bg-gray-30">
          <MailIcon className="h-5 w-5 stroke-gray-80 stroke-1.5" />
        </div>
      );
    }
    if (variant === 'matrix') {
      return (
        <div className="flex shrink-0 items-center justify-center w-10 h-10 rounded-xl bg-gray-30">
          <MatrixIcon className="h-5 w-5 fill-current text-gray-80" />
        </div>
      );
    }
    return <Avatar size="sm" name={c.displayName} />;
  };

  return (
    <div className="flex min-w-0 flex-col gap-2" data-testid="invite-participants-field" data-search-term={searchTerm}>
      {selectedCandidates.length > 0 && (
        <div className="relative">
          <div className="flex flex-row gap-3 overflow-x-auto pb-1 [&::-webkit-scrollbar]:hidden">
            {selectedCandidates.map((c) => (
              <div key={c.userId} className="relative flex shrink-0 flex-col items-center gap-1">
                {renderChipAvatar(c, displayNameMap)}
                <Tooltip
                  label={!matrixOnly && isEmailFormat(c.userId) ? c.userId : c.displayName}
                  placement="top"
                  inPortal
                  className="max-w-[48px]"
                >
                  <span className="block overflow-hidden truncate whitespace-nowrap text-size-xs text-black">
                    {!matrixOnly && isEmailFormat(c.userId) ? c.userId : c.displayName}
                  </span>
                </Tooltip>
                <button
                  type="button"
                  onClick={() => onToggle(c.userId)}
                  className="absolute -top-1 right-0 flex h-3.5 w-3.5 items-center justify-center rounded bg-white text-gray-80 shadow"
                  aria-label={t('remove_member', { name: c.displayName })}
                >
                  <CloseSmallIcon className="h-3 w-3" />
                </button>
              </div>
            ))}
          </div>
          <div className="pointer-events-none absolute right-0 top-0 h-full w-6 bg-gradient-to-l from-white to-transparent" />
        </div>
      )}

      <span className="text-size-md font-medium text-black">
        {useOrganisationList
          ? t('schedule_meeting.workspace_members_label')
          : t('schedule_meeting.suggested_users_label')}
      </span>

      {excludeRoomId && !excludeRoomMembersReady ? (
        <CenteredSpinner className="!mb-0 py-4" />
      ) : useOrganisationList ? (
        <OrganisationMembersList
          searchTerm={searchTerm}
          participants={participants}
          onToggle={onToggle}
          excludeUserIds={existingRoomMemberIds}
        />
      ) : (
        <SuggestedMembersList
          searchTerm={searchTerm}
          participants={participants}
          onToggle={onToggle}
          excludeUserIds={existingRoomMemberIds}
        />
      )}
    </div>
  );
};

export default InviteParticipantsField;
